/**
 * The sentences the ANTICIPATE panel says, and nothing else.
 *
 * Pure, so the wording is testable without a DOM and so a reviewer can read the
 * product's voice in one place. `Insights.tsx`'s own logic module makes the
 * same split for the same reason.
 *
 * ===========================================================================
 * A PREDICTION RENDERED WITH THE CONFIDENCE OF A MEASUREMENT IS THE DEFECT
 * ===========================================================================
 * Everything on this panel is about a day that has not happened. So:
 *
 *   - NO PERCENTAGE, and no number that could be mistaken for one. The server
 *     grades likelihood into two words because those are the two the rows can
 *     support; inventing "87% likely" from them would be exactly the
 *     "unsupported precision" the organization's own forecasting doctrine
 *     (`FORECAST_MODEL.transparencyRule`) forbids.
 *   - EVERY ROW SAYS WHAT WOULD MAKE IT WRONG. A forecast whose falsifier is
 *     hidden reads as a verdict. `falsifiedBy` is not fine print here; it is
 *     the thing the reader is being asked to do.
 *   - `undetermined` IS SPELLED OUT, never rounded up to a warning. "We cannot
 *     say" is a real answer and the vocabulary module keeps whole concepts in
 *     that state precisely so nothing invents one.
 *
 * ===========================================================================
 * AND NO RED
 * ===========================================================================
 * The tone rule — "encourage investigation, not anxiety; no giant red alert
 * wall" — binds harder here than on findings. Nothing on this panel has
 * happened yet, and a screen that renders the future in the same colour as a
 * lapse teaches a reader to ignore both. {@link toneFor} has no emphatic
 * branch, and there is no severity on a forecast for one to key off.
 */

import type {
  ForecastDisclosure,
  ForecastLikelihood,
  ForecastOutcome,
  ForecastSummary,
} from './forecastsClient.js';

/**
 * The pill variants this panel is allowed to use, and the list is the point:
 * `urgent` is not in it. `Severity` in the design tokens offers it; nothing on
 * this panel may reach it, because nothing on this panel has happened.
 */
export type ForecastTone = 'plain' | 'sky';

export function toneFor(likelihood: ForecastLikelihood): ForecastTone {
  return likelihood === 'certain_absent_action' ? 'sky' : 'plain';
}

/**
 * The likelihood, as a phrase a person would say.
 *
 * "Unless someone acts" rather than "certain": the certainty is about the
 * ARITHMETIC, not about the outcome, and a reader who takes "certain" at face
 * value would reasonably conclude there was no point acting — which is the
 * opposite of what the row is for.
 */
export function likelihoodLabel(likelihood: ForecastLikelihood): string {
  return likelihood === 'certain_absent_action' ? 'Unless someone acts' : 'We cannot say';
}

/**
 * How far off, in words. Deliberately coarse: a forecast is not more useful
 * for being read to the day, and "in 23 days" invites a precision the horizon
 * does not have once a worker's role, program or employment status can change
 * inside it.
 */
export function horizonPhrase(daysAhead: number): string {
  if (daysAhead <= 0) return 'today';
  if (daysAhead === 1) return 'tomorrow';
  if (daysAhead <= 7) return `in ${daysAhead} days`;
  if (daysAhead <= 14) return 'in about two weeks';
  if (daysAhead <= 35) return 'in about a month';
  return 'in over a month';
}

/** A disclosed name, or the honest word for why there is not one. */
export function nameOrReason(disclosure: ForecastDisclosure): string {
  switch (disclosure.disclosure) {
    case 'shown':
      return disclosure.label;
    case 'withheld':
      // WITHHELD IS NOT UNKNOWN. The row exists and policy forbids naming it
      // here; saying "unknown" would be a different and false claim.
      return 'Someone you may not see';
    case 'absent':
      return 'A record that is no longer there';
  }
}

/** The headline sentence for one row. */
export function forecastSentence(row: ForecastSummary): string {
  const who = nameOrReason(row.subject);
  const what = nameOrReason(row.credentialTypeLabel);
  return row.transitionReason === 'credential_expiry'
    ? `${who} stops meeting the ${what} requirement ${horizonPhrase(row.daysAhead)}.`
    : `A new ${what} requirement starts to apply to ${who} ${horizonPhrase(row.daysAhead)}, and nothing is on file.`;
}

/** What became of it, once it is no longer live. */
export function outcomeLabel(outcome: ForecastOutcome): string | null {
  switch (outcome) {
    case 'pending':
      return null;
    case 'realized':
      return 'It happened';
    case 'averted':
      // NOT "we were wrong". Somebody acted, which is what the row was for.
      return 'Someone acted in time';
    case 'withdrawn':
      return 'No longer applies';
  }
}

/**
 * The line under the list, and it must not read as a refusal when nothing was
 * refused. `withheld` counts rows this reader may not have; zero of them is a
 * real measured zero and says nothing at all.
 */
export function withheldLine(withheld: number | undefined): string | null {
  if (withheld === undefined || withheld === 0) return null;
  return withheld === 1
    ? '1 more is withheld from you — it exists, and reading it needs permission you do not hold.'
    : `${withheld} more are withheld from you — they exist, and reading them needs permission you do not hold.`;
}

/**
 * WHOSE forecasts these are. Read off the server's answer, never re-derived
 * from the reader's permissions: a narrowed answer that labels itself
 * organization-wide is how "nobody is heading for a lapse" gets said about a
 * caseload of two.
 */
export function scopeLine(scope: 'organization' | 'caseload' | undefined): string {
  if (scope === 'organization') return 'Everyone in your organization.';
  if (scope === 'caseload') return 'You and the people who report to you.';
  return '';
}

/** The empty state. There is nothing wrong with an empty forecast list. */
export function emptyLine(scope: 'organization' | 'caseload' | undefined): string {
  return scope === 'caseload'
    ? 'Nothing is due to change for your people in the next two months.'
    : 'Nothing is due to change in the next two months.';
}
